import React from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";
import { Plus, ArrowRight, Users, KeyRound, X, Clock, Sparkles, Upload, Building2, Save } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

function relativeTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";
  const diff = Date.now() - then;
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function ActivityPill({ count }) {
  let cls = "bg-[var(--sw-bg-soft)] text-[var(--sw-ink-soft)] border-[var(--sw-line)]";
  let label = "0 this week";
  if (count >= 1 && count <= 3) {
    cls = "bg-[var(--sw-amber)]/20 text-[var(--sw-primary-700)] border-[var(--sw-amber)]/40";
    label = `${count} this week`;
  } else if (count >= 4) {
    cls = "bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]";
    label = `${count} this week`;
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-1 border ${cls}`}>
      <Sparkles size={12} /> {label}
    </span>
  );
}

export default function TherapistDashboard() {
  const { actor, refresh } = useAuth();
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [showBulk, setShowBulk] = React.useState(false);
  const [showOrg, setShowOrg] = React.useState(false);
  const [form, setForm] = React.useState({ display_name: "", username: "", pin: "" });
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/students"); setStudents(data); } catch (e) { /* ignore */ }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/students", form);
      toast.success("Student added.");
      setShowAdd(false);
      setForm({ display_name: "", username: "", pin: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add student");
    }
    setBusy(false);
  };

  return (
    <div data-testid={TID.therapistDashboard} className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Therapist workspace</div>
          <h1 className="display mt-1 text-4xl md:text-5xl font-extrabold">Your caseload</h1>
          <p className="mt-2 text-[var(--sw-ink-soft)] flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[var(--sw-ink)]">{actor?.name}</span>
            {actor?.organization ? (
              <>
                <span aria-hidden className="opacity-40">·</span>
                <span className="inline-flex items-center gap-1.5"><Building2 size={14} className="text-[var(--sw-primary)]" /> {actor.organization}</span>
              </>
            ) : (
              <>
                <span aria-hidden className="opacity-40">·</span>
                <span className="italic text-[var(--sw-ink-soft)]">No organization set yet</span>
              </>
            )}
            <button
              type="button"
              data-testid="edit-organization-btn"
              className="text-xs underline text-[var(--sw-primary)] font-semibold"
              onClick={() => setShowOrg((v) => !v)}
            >
              {showOrg ? "Cancel" : (actor?.organization ? "Edit profile" : "Add organization / district")}
            </button>
          </p>
          <p className="mt-2 text-[var(--sw-ink-soft)]">Curate sensory boxes per student and review their selection patterns.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="bulk-import-btn" onClick={() => { setShowBulk((v) => !v); setShowAdd(false); }} className="sw-pill h-12">
            {showBulk ? <X size={16} /> : <Upload size={16} />} {showBulk ? "Close" : "Bulk import"}
          </button>
          <button data-testid={TID.addStudentBtn} onClick={() => { setShowAdd((v) => !v); setShowBulk(false); }} className="sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]">
            {showAdd ? <X size={16} /> : <Plus size={16} />} {showAdd ? "Close" : "Add student"}
          </button>
        </div>
      </header>

      {showOrg && (
        <OrganizationEditor
          current={actor?.organization || ""}
          currentName={actor?.name || ""}
          onSaved={async () => { await refresh(); setShowOrg(false); }}
          onCancel={() => setShowOrg(false)}
        />
      )}

      {showBulk && (
        <BulkImport onDone={() => { setShowBulk(false); load(); }} />
      )}

      {showAdd && (
        <form onSubmit={add} className="sw-card p-6 grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-sm font-semibold">Display name</span>
            <input data-testid={TID.newStudentName} required value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="e.g. Alex K." className="mt-1 w-full h-12 px-4 rounded-xl border border-[var(--sw-line)] bg-white" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">Username</span>
            <input data-testid={TID.newStudentUsername} required minLength={2} value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase().replace(/\s+/g, "") }))} placeholder="alexk" className="mt-1 w-full h-12 px-4 rounded-xl border border-[var(--sw-line)] bg-white" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">4-digit PIN</span>
            <input data-testid={TID.newStudentPin} required inputMode="numeric" pattern="[0-9]{4,8}" minLength={4} maxLength={8} value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="4321" className="mt-1 w-full h-12 px-4 rounded-xl border border-[var(--sw-line)] bg-white" />
          </label>
          <div className="md:col-span-3 flex gap-3">
            <button data-testid={TID.newStudentSubmit} disabled={busy} className="sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]">{busy ? "Adding…" : "Add student"}</button>
            <button type="button" data-testid={TID.newStudentCancel} onClick={() => setShowAdd(false)} className="sw-pill h-12">Cancel</button>
          </div>
        </form>
      )}

      <section>
        <div className="flex items-center gap-3 mb-4"><Users className="text-[var(--sw-primary)]" /><h2 className="display text-2xl font-bold">Your students</h2></div>
        {loading ? (
          <div className="text-[var(--sw-ink-soft)]">Loading…</div>
        ) : students.length === 0 ? (
          <div className="sw-card p-8 text-center text-[var(--sw-ink-soft)]">No students yet. Click &quot;Add student&quot; above.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {students.map((s) => (
              <div key={s.id} data-testid={TID.studentRow(s.id)} className="sw-card p-5 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="display text-xl font-bold leading-tight break-words">{s.display_name}</div>
                    <div className="text-sm text-[var(--sw-ink-soft)] flex items-center gap-1 mt-0.5 break-all"><KeyRound size={14} className="shrink-0" /> @{s.username}</div>
                  </div>
                  <ActivityPill count={s.selections_this_week || 0} />
                </div>

                <div className="flex items-center gap-2 text-xs text-[var(--sw-ink-soft)] flex-wrap">
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> Last opened {relativeTime(s.last_active_at)}</span>
                  <span>·</span>
                  <span>{(s.enabled_intervention_ids?.length || 0)} bubble{(s.enabled_intervention_ids?.length || 0) === 1 ? "" : "s"} enabled</span>
                </div>

                <Link to={`/therapist/students/${s.id}`} data-testid={TID.studentOpen(s.id)} className="sw-pill h-11 mt-2 self-start">
                  Open <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}


function OrganizationEditor({ current, currentName, onSaved, onCancel }) {
  const [name, setName] = React.useState(currentName || "");
  const [org, setOrg] = React.useState(current || "");
  const [busy, setBusy] = React.useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/profile", { name: name.trim(), organization: org.trim() });
      toast.success("Profile updated.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update profile");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="sw-card p-6 grid md:grid-cols-2 gap-3" data-testid="organization-editor">
      <label className="block md:col-span-1">
        <span className="text-sm font-semibold">Your name &amp; credentials</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g. Jordan Lee, OTR/L"
          className="mt-1 w-full h-12 px-4 rounded-xl border border-[var(--sw-line)] bg-white"
          data-testid="organization-name-input"
        />
      </label>
      <label className="block md:col-span-1">
        <span className="text-sm font-semibold">Organization / School district</span>
        <input
          value={org}
          onChange={(e) => setOrg(e.target.value)}
          placeholder="e.g. Riverbend Unified School District"
          className="mt-1 w-full h-12 px-4 rounded-xl border border-[var(--sw-line)] bg-white"
          data-testid="organization-input"
        />
      </label>
      <div className="md:col-span-2 flex gap-2">
        <button
          disabled={busy}
          className="sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
          data-testid="organization-save-btn"
        >
          <Save size={16} /> {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="sw-pill h-12">Cancel</button>
        <div className="ml-auto self-center text-xs text-[var(--sw-ink-soft)]">
          Appears in your PDF report headers for IEP / MTSS documentation.
        </div>
      </div>
    </form>
  );
}

// --- Bulk CSV import ---------------------------------------------------------
function parseCsv(text) {
  // Minimal CSV parser: supports optional header row, commas, quoted values.
  const rows = [];
  let i = 0;
  const len = text.length;
  let field = "";
  let row = [];
  let inQuotes = false;
  while (i < len) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i += 1; continue; }
      field += ch; i += 1; continue;
    }
    if (ch === '"') { inQuotes = true; i += 1; continue; }
    if (ch === ",") { row.push(field); field = ""; i += 1; continue; }
    if (ch === "\r") { i += 1; continue; }
    if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; i += 1; continue; }
    field += ch; i += 1;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim().length > 0));
}

function BulkImport({ onDone }) {
  const [rawCsv, setRawCsv] = React.useState("");
  const [preview, setPreview] = React.useState([]); // {display_name, username, pin, error?}
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const parse = (text) => {
    const rows = parseCsv(text);
    if (rows.length === 0) { setPreview([]); return; }
    // Detect header row
    const first = rows[0].map((c) => (c || "").trim().toLowerCase());
    const hasHeader = first.includes("display_name") || first.includes("name") || first.includes("username");
    let idxName = 0, idxUser = 1, idxPin = 2;
    let data = rows;
    if (hasHeader) {
      idxName = first.findIndex((c) => c === "display_name" || c === "name");
      idxUser = first.findIndex((c) => c === "username" || c === "user");
      idxPin = first.findIndex((c) => c === "pin" || c === "password");
      if (idxName < 0) idxName = 0;
      if (idxUser < 0) idxUser = 1;
      if (idxPin < 0) idxPin = 2;
      data = rows.slice(1);
    }
    const parsed = data.map((r) => {
      const display_name = (r[idxName] || "").trim();
      const username = (r[idxUser] || "").trim().toLowerCase().replace(/\s+/g, "");
      const pin = (r[idxPin] || "").trim();
      let error = "";
      if (!display_name) error = "Missing name";
      else if (!username || username.length < 2) error = "Username must be at least 2 chars";
      else if (!/^[0-9]{4,8}$/.test(pin)) error = "PIN must be 4–8 digits";
      return { display_name, username, pin, error };
    });
    setPreview(parsed);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setRawCsv(text);
      parse(text);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const onTextChange = (e) => {
    const t = e.target.value;
    setRawCsv(t);
    parse(t);
    setResult(null);
  };

  const submit = async () => {
    const valid = preview.filter((p) => !p.error);
    if (valid.length === 0) {
      toast.error("No valid rows to import.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/students/bulk", {
        students: valid.map(({ display_name, username, pin }) => ({ display_name, username, pin })),
      });
      setResult(data);
      toast.success(`${data.created_count} student${data.created_count === 1 ? "" : "s"} added${data.skipped_count ? `, ${data.skipped_count} skipped` : ""}.`);
      if (data.skipped_count === 0) {
        // give the toast a beat, then close
        setTimeout(() => { onDone && onDone(); }, 800);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk import failed");
    }
    setBusy(false);
  };

  const sampleCsv = "display_name,username,pin\nAlex K.,alexk,4321\nMorgan T.,morgant,8765\nSam R.,samr,1122\n";
  const validCount = preview.filter((p) => !p.error).length;
  const errorCount = preview.length - validCount;

  return (
    <div className="sw-card p-6 space-y-4" data-testid="bulk-import-panel">
      <div>
        <div className="display text-xl font-bold">Bulk student import</div>
        <p className="text-sm text-[var(--sw-ink-soft)]">
          Upload a CSV or paste rows. Columns: <code>display_name, username, pin</code> (4–8 digit PIN).
          Header row is optional. Existing usernames are skipped safely.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block">
            <span className="text-sm font-semibold">Upload CSV file</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="mt-1 w-full text-sm"
              data-testid="bulk-file-input"
            />
          </label>
          <button
            type="button"
            className="text-xs underline text-[var(--sw-primary)]"
            onClick={() => { setRawCsv(sampleCsv); parse(sampleCsv); }}
            data-testid="bulk-sample-btn"
          >
            Insert sample rows
          </button>
        </div>
        <label className="block">
          <span className="text-sm font-semibold">…or paste CSV rows</span>
          <textarea
            value={rawCsv}
            onChange={onTextChange}
            rows={6}
            placeholder={sampleCsv}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-[var(--sw-line)] bg-white font-mono text-xs"
            data-testid="bulk-textarea"
          />
        </label>
      </div>

      {preview.length > 0 && (
        <div className="border border-[var(--sw-line)] rounded-2xl overflow-hidden">
          <div className="bg-[var(--sw-bg-soft)] px-4 py-2 text-xs uppercase tracking-widest text-[var(--sw-ink-soft)] flex items-center justify-between">
            <span>Preview · {validCount} ready · {errorCount ? `${errorCount} needs fixing` : "0 issues"}</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white sticky top-0">
                <tr className="text-left text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Username</th>
                  <th className="px-4 py-2">PIN</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((p, i) => (
                  <tr key={i} className="border-t border-[var(--sw-line)]">
                    <td className="px-4 py-2 truncate max-w-[220px]">{p.display_name || <span className="text-[var(--sw-ink-soft)] italic">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.username || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.pin || "—"}</td>
                    <td className="px-4 py-2">
                      {p.error ? (
                        <span className="text-xs font-semibold text-[var(--gusty-text)]">✕ {p.error}</span>
                      ) : (
                        <span className="text-xs font-semibold text-[color:#22857B]">✓ Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && result.skipped_count > 0 && (
        <div className="rounded-2xl border border-[var(--gusty-border)] bg-[var(--gusty-bg)] text-[var(--gusty-text)] p-4 text-sm" data-testid="bulk-skipped">
          <div className="font-semibold mb-2">{result.skipped_count} row(s) skipped:</div>
          <ul className="list-disc ml-5 space-y-1">
            {result.skipped.map((s, i) => (
              <li key={i}><code>{s.username || "?"}</code> — {s.reason}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={submit}
          disabled={busy || validCount === 0}
          className="sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
          data-testid="bulk-submit-btn"
        >
          <Upload size={16} /> {busy ? "Importing…" : `Import ${validCount} student${validCount === 1 ? "" : "s"}`}
        </button>
        <button type="button" onClick={onDone} className="sw-pill h-12">Close</button>
      </div>
    </div>
  );
}
